import {ChronologicalCommentsSort, isChronologicalCommentsSort} from '@github-ui/commenting/ChronologicalCommentsSort'
import {useLocalStorage} from '@github-ui/use-safe-storage/local-storage'
import {createContext, type ReactNode, use, useCallback, useOptimistic, useState, useTransition} from 'react'

import {usePullRequestAnalytics} from '../hooks/use-pull-request-analytics'

export type TimelineSort = ChronologicalCommentsSort

export const TIMELINE_SORT_STORAGE_KEY = 'pull-request-activity-sort'

// Without a provider the timeline stays oldest first.
const TimelineSortContext = createContext<TimelineSort>(ChronologicalCommentsSort.OldestFirst)
const SelectedTimelineSortContext = createContext<TimelineSort>(ChronologicalCommentsSort.OldestFirst)
const SetTimelineSortContext = createContext<(sort: TimelineSort) => void>(() => {})

// The timeline reads this state rather than local storage so a sort change can be a transition, which keeps the
// current list up while the other order loads.
export function TimelineSortProvider({children}: {children: ReactNode}) {
  const [savedSort, setSavedSort] = useLocalStorage<TimelineSort>(
    TIMELINE_SORT_STORAGE_KEY,
    ChronologicalCommentsSort.NewestFirst,
    {validator: isChronologicalCommentsSort},
  )
  const [sort, setSort] = useState(savedSort)
  const [selectedSort, setSelectedSort] = useOptimistic(sort)
  const [, startTransition] = useTransition()
  const {sendPullRequestAnalyticsEvent} = usePullRequestAnalytics()

  const changeSort = useCallback(
    (next: TimelineSort) => {
      setSavedSort(next)
      startTransition(() => {
        setSelectedSort(next)
        setSort(next)
      })
      sendPullRequestAnalyticsEvent('overview.activity_timeline_sort_change', 'OVERVIEW_TIMELINE_SORT', {sort: next})
    },
    [setSavedSort, setSelectedSort, sendPullRequestAnalyticsEvent],
  )

  return (
    <SetTimelineSortContext value={changeSort}>
      <SelectedTimelineSortContext value={selectedSort}>
        <TimelineSortContext value={sort}>{children}</TimelineSortContext>
      </SelectedTimelineSortContext>
    </SetTimelineSortContext>
  )
}

export function useTimelineSort(): TimelineSort {
  return use(TimelineSortContext)
}

/** Updates as soon as the reader picks a sort. `useTimelineSort` follows once that order loads. */
export function useSelectedTimelineSort(): TimelineSort {
  return use(SelectedTimelineSortContext)
}

export function useSetTimelineSort(): (sort: TimelineSort) => void {
  return use(SetTimelineSortContext)
}
